import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api, errMsg } from "../lib/api";
import { MatchCard } from "./MatchCard";
import { RoundStatus } from "./RoundStatus";

const toLocal = (matches) =>
  Object.fromEntries(matches.filter((m) => m.prediction).map((m) => [m.match_id, { ...m.prediction }]));

const Counter = ({ label, used, max, color }) => (
  <div className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg border ${used > max ? "border-red-500 text-red-400" : "border-[#26334D]"}`} data-testid={`counter-${label.toLowerCase()}`}>
    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{label}</span>
    <span className={`font-display text-lg sm:text-xl font-bold ${color}`}>{used}/{max}</span>
  </div>
);

export const RoundPredictions = ({ tid, round, info, limits, onSaved, onLive }) => {
  const [matches, setMatches] = useState(null);
  const [preds, setPreds] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/tournaments/${tid}/matches`, { params: { round } }).then((r) => { setMatches(r.data); setPreds(toLocal(r.data)); });

  useEffect(() => {
    setMatches(null);
    load().catch((e) => { toast.error(errMsg(e)); setMatches([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tid, round]);

  const inProgress = matches?.some((m) => ["IN_PLAY", "PAUSED"].includes(m.status) ||
    (["TIMED", "SCHEDULED"].includes(m.status) && new Date(m.utc_date) <= new Date()));

  const hasBar = !!matches?.some((m) => m.is_open);
  useEffect(() => {
    document.body.classList.toggle("has-mobile-bar", hasBar);
    return () => document.body.classList.remove("has-mobile-bar");
  }, [hasBar]);

  useEffect(() => {
    if (!inProgress) return;
    const id = setInterval(() => {
      api.get(`/tournaments/${tid}/matches`, { params: { round } }).then((r) => setMatches(r.data)).catch(() => {});
      onLive?.();
    }, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, tid, round]);

  const counts = useMemo(() => {
    const v = Object.values(preds);
    return {
      fisse: v.filter((p) => p.pick_type === "fissa").length,
      doppie: v.filter((p) => p.pick_type === "doppia").length,
      esatti: v.filter((p) => p.exact_home != null && p.exact_away != null).length,
    };
  }, [preds]);

  const update = (id, next) => {
    const copy = { ...preds };
    if (!next) delete copy[id];
    else {
      const prev = preds[id];
      if (next.pick_type !== prev?.pick_type) {
        const key = next.pick_type === "fissa" ? "fisse" : "doppie";
        if (!limits.unlimited && counts[key] >= limits[key]) return toast.error(`Hai raggiunto il limite di ${key} per questa giornata`);
      }
      const hadExact = prev?.exact_home != null && prev?.exact_away != null;
      const hasExact = next.exact_home != null && next.exact_away != null;
      if (hasExact && !hadExact && counts.esatti >= limits.esatti)
        return toast.error("Hai raggiunto il limite di risultati esatti");
      copy[id] = next;
    }
    setPreds(copy);
  };

  const save = async () => {
    const open = matches.filter((m) => m.is_open);
    const predictions = open
      .filter((m) => preds[m.match_id])
      .map((m) => {
        const p = preds[m.match_id];
        const exact = p.exact_home != null && p.exact_away != null;
        return { match_id: m.match_id, pick_type: p.pick_type, pick: p.pick, exact_home: exact ? p.exact_home : null, exact_away: exact ? p.exact_away : null };
      });
    const remove = open.filter((m) => m.prediction && !preds[m.match_id]).map((m) => m.match_id);
    setBusy(true);
    try {
      const r = await api.put(`/tournaments/${tid}/predictions`, { predictions, remove });
      toast.success(`Pronostici salvati: ${r.data.saved}${r.data.removed ? ` · rimossi ${r.data.removed}` : ""}`);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e, "Pronostici non validi"));
    } finally {
      setBusy(false);
    }
  };

  if (!matches) return <div className="card-surface h-64 animate-pulse" />;
  if (!matches.length) return <p className="text-slate-400" data-testid="no-matches">Nessuna partita per questa giornata.</p>;
  const anyOpen = matches.some((m) => m.is_open);

  return (
    <div className="space-y-4 sm:space-y-6">
      <RoundStatus info={info} />
      <div className="sticky top-16 z-20 bg-[#0A0D14] py-2 sm:py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-full sm:w-auto gap-2">
          <Counter label="Fisse" used={counts.fisse} max={limits.unlimited ? "∞" : limits.fisse} color="text-emerald-400" />
          <Counter label="Doppie" used={counts.doppie} max={limits.unlimited ? "∞" : limits.doppie} color="text-blue-400" />
          <Counter label="Esatti" used={counts.esatti} max={limits.esatti} color="text-violet-400" />
        </div>
        {anyOpen && (
          <Button onClick={save} disabled={busy} data-testid="save-predictions-button" className="hidden sm:inline-flex h-11 px-8 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0D14] font-bold uppercase tracking-wider">
            {busy ? "Salvataggio…" : "Salva pronostici"}
          </Button>
        )}
      </div>
      {anyOpen && (
        <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 px-3 pt-3 bg-[#0A0D14]/95 border-t border-[#26334D] pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <Button onClick={save} disabled={busy} data-testid="save-predictions-button-mobile" className="w-full h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0D14] font-bold uppercase tracking-wider">
            {busy ? "Salvataggio…" : "Salva pronostici"}
          </Button>
        </div>
      )}
      {inProgress && (
        <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-red-400" data-testid="live-indicator">
          <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> Risultati in diretta · aggiornamento ogni minuto
        </p>
      )}
      <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
        {matches.map((m, i) => (
          <MatchCard key={m.match_id} m={m} index={i} pred={preds[m.match_id]} onChange={(p) => update(m.match_id, p)} />
        ))}
      </div>
    </div>
  );
};
